import { useReveal } from "@/lib/hooks";
import { MapPin } from "lucide-react";

const PHOTO_1 = "/wedding-photos/24597128-5ae9-469a-89f7-1d16be3e5a56.jpeg";
const PHOTO_2 = "/wedding-photos/2aa34552-3dd7-4ba3-b776-a354604d5fb8.jpeg";
const PHOTO_3 = "/wedding-photos/IMG_3907.jpeg";
const PHOTO_4 = "/wedding-photos/IMG_4746.jpeg";
const PHOTO_5 = "/wedding-photos/IMG_4806.jpeg";
const PHOTO_6 = "/wedding-photos/IMG_7542.jpeg";
const PHOTO_7 = "/wedding-photos/IMG_7567.jpeg";

const events = [
  {
    time: "1:00pm",
    end: "1:45pm",
    title: "Ceremony",
    place: "St. Joseph's Church",
    address: "Presentation Road, Galway",
    description:
      "Please arrive by 12:45pm to be seated. We exchange our vows in the heart of Galway city, surrounded by family and friends.",
    parking: [
      "Some on-street parking is available on the streets around the church.",
      "Galway Cathedral Car Park — a large car park, a 10 minute walk from St. Joseph's.",
      "Mill Street Car Park — a 5 minute walk from the church.",
    ],
    image: "/wedding-photos/IMG_4936.jpeg",
  },
  {
    time: "3:00pm",
    title: "Drinks Reception",
    place: "The River Room, Glenlo Abbey",
    address: "Bushy Park, Galway",
    description:
      "Toast with us in The River Room overlooking Lough Corrib — bubbles, canapés, and a few surprises.",
    image: PHOTO_2,
  },
  {
    time: "5:00pm",
    title: "Guests Called for Dinner",
    place: "The Corrib Room, Glenlo Abbey",
    address: "Glenlo Abbey Estate",
    description:
      "Find your seat — table plans will be on display.",
    image: PHOTO_3,
  },
  {
    time: "6:00pm",
    title: "Dinner",
    place: "The Corrib Room, Glenlo Abbey",
    address: "Glenlo Abbey Estate",
    description:
      "Five courses, a glass or two of something nice, and a few words from the brave souls volunteering to make speeches. Pace yourselves — there's dancing later.",
    image: "/wedding-photos/IMG_5467.jpeg",
  },
  {
    time: "9:30pm",
    title: "Band Starts",
    place: "The Corrib Room, Glenlo Abbey",
    address: "Glenlo Abbey Estate",
    description:
      "First dance, then the band takes over. Comfortable shoes very much encouraged.",
    image: PHOTO_5,
  },
  {
    time: "12:00am",
    title: "DJ Takes Over",
    place: "The Corrib Room, Glenlo Abbey",
    address: "Glenlo Abbey Estate",
    description:
      "The DJ keeps the dance floor going right through into the early hours.",
    image: PHOTO_1,
  },
  {
    time: "2:00am",
    title: "Music Ends · Bar Closed",
    place: "Glenlo Abbey",
    address: "Estate grounds",
    description:
      "The official festivities wind down — but the residents' bar stays open until 4am, just in case you've still got it in you.",
    image: PHOTO_7,
  },
];

function TimelineItem({ event, index }) {
  const ref = useReveal();
  const reverse = index % 2 === 1;
  return (
    <div
      ref={ref}
      className="reveal grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center mb-12 md:mb-16 last:mb-0"
      data-testid={`schedule-item-${index}`}
    >
      <div className={`md:col-span-4 ${reverse ? "md:order-2" : ""}`}>
        <div className="aspect-[4/3] overflow-hidden bg-[#F3EFE9]">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
          />
        </div>
      </div>
      <div className={`md:col-span-8 ${reverse ? "md:order-1 md:pr-10" : "md:pl-10"}`}>
        <div className="flex items-baseline gap-3 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#A3C1DA] translate-y-[-3px]" />
          <span className="font-display text-2xl md:text-3xl text-[#6A1E2D] tabular-nums">
            {event.time}
            {event.end && (
              <span className="text-[#4A4747]/60 text-lg md:text-xl"> – {event.end}</span>
            )}
          </span>
        </div>
        <h3 className="font-display text-3xl md:text-5xl text-[#1A1818] font-light leading-tight mb-4">
          {event.title}
        </h3>
        <div className="flex items-start gap-3 mb-4 text-[#4A4747]">
          <MapPin className="w-4 h-4 mt-1 text-[#6A1E2D] shrink-0" strokeWidth={1.4} />
          <div>
            <p className="font-medium text-[#1A1818]">{event.place}</p>
            <p className="text-sm">{event.address}</p>
          </div>
        </div>
        <p className="font-body text-[#4A4747] leading-relaxed">{event.description}</p>
        {Array.isArray(event.parking) && event.parking.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#6A1E2D]/15">
            <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-3">Parking</p>
            <ul className="space-y-2 font-body text-sm text-[#4A4747]">
              {event.parking.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#A3C1DA] select-none">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Schedule() {
  return (
    <section
      id="schedule"
      data-testid="schedule-section"
      className="bg-[#F3EFE9]/60 py-24 md:py-32 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-4">
            The order of the day
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl text-[#1A1818] leading-[1.05]">
            A day along the
            <span className="italic"> Corrib</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[#4A4747]">
            Friday, 4 June 2027. Here's how the day will unfold — from the
            ceremony in Galway city to the last song at Glenlo Abbey.
          </p>
        </div>

        <div className="relative">
          <div
            className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[#6A1E2D]/15"
            aria-hidden
          />
          {events.map((e, i) => (
            <TimelineItem key={e.title} event={e} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
